import React from "react";

/**
 * Top-level error boundary — the last line of defence against a blank
 * white screen. Without this, any uncaught render error anywhere in the
 * tree unmounts the entire app with zero indication to the user of what
 * happened and no way to recover short of guessing to hit refresh.
 *
 * Deliberately minimal and dependency-free (no Tailwind utility classes,
 * no other app components) — the fallback itself has to be bulletproof,
 * since there's nothing left to catch an error thrown while rendering it.
 */

// When the app was last reloaded to recover from a stale bundle.
const RELOADED_AT = "fnd_stale_chunk_reload";

/**
 * Was this a page asking for a file the deploy has already replaced?
 *
 * Every route in App.js is lazy(), and a production build names its chunks
 * by content hash. A deploy therefore renames all of them, so a tab that
 * was open when it landed asks for `1234.oldhash.chunk.js` the next time
 * the agent opens a screen they hadn't visited yet — gets a 404, throws,
 * and lands here. Nothing is wrong with the page; it is simply a minute
 * out of date. Pushes to main deploy straight to production in this
 * codebase, so this is not a rare event.
 */
const isStaleBundle = (error) =>
  error?.name === "ChunkLoadError" ||
  /Loading (?:CSS )?chunk \S+ failed|(?:Failed to fetch|error loading) dynamically imported module/i
    .test(error?.message || "");

/** Reload, unless we just did. Returns whether it is reloading.
 *
 *  The guard is the whole point: a render error that merely LOOKS like a
 *  stale chunk would otherwise reload forever, which is worse than the
 *  message it is trying to avoid. Ten seconds is long enough to cover the
 *  reload itself and short enough that a second deploy later in the same
 *  session still recovers on its own. If sessionStorage is unavailable
 *  (private mode, blocked storage) there is nowhere to keep the guard, so
 *  we don't auto-reload at all and the button below does the job.
 */
function reloadForStaleBundle() {
  let last;
  try {
    last = Number(window.sessionStorage.getItem(RELOADED_AT) || 0);
    if (Date.now() - last < 10_000) return false;
    window.sessionStorage.setItem(RELOADED_AT, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, stale: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, stale: isStaleBundle(error) };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Unhandled error caught by ErrorBoundary:", error, info?.componentStack);
    if (isStaleBundle(error)) reloadForStaleBundle();
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "Georgia, 'Cormorant Garamond', serif",
          background: "#f9f5ee",
          color: "#1a2e22",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", color: "#a0653c", marginBottom: 16 }}>
            Something went wrong
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 500, marginBottom: 16, lineHeight: 1.2 }}>
            {this.state.stale ? "Foundation has just been updated." : "This page hit a snag."}
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: "#4a4a4a", marginBottom: 28, fontFamily: "system-ui, sans-serif" }}>
            {this.state.stale ? (
              "This tab was open while a new version went out, so it asked for files that had already been replaced. Reload and it will pick up the new one. Nothing has been lost."
            ) : (
              <>
                Reloading usually fixes it. If it keeps happening, drop us a line at{" "}
                <a href="mailto:hello@foundationagent.co.uk" style={{ color: "#a0653c" }}>
                  hello@foundationagent.co.uk
                </a>{" "}
                and we'll get on it.
              </>
            )}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#1a2e22",
              color: "#f9f5ee",
              border: "none",
              padding: "12px 28px",
              fontSize: 13,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
